import { ApproverTypeEnum, CurrencyEnum, WorkflowConditionEnum } from "@finway-group/shared/lib/models"
import { Button, Col, Form, Row, Steps } from "antd"
import React, { useEffect, useRef } from "react"
import { Plus as PlusIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { WorkflowFormStep } from "~Components/forms/workflowForm/workflowStep.form"
import { useLoggedInEmployee } from "~Shared/hooks/employee.hooks"
import { EmployeeService } from "~Shared/services"
import useStateIfMounted from "~Shared/utils/hooks/useStateIfMounted"
import { WorkflowFormTypeEnum, WorkflowFormValuesInterface } from "./workflowFormValues.interface"

interface WorkflowFormStepsProps {
    formValues: WorkflowFormValuesInterface
    setFormValues: (formValues: Partial<WorkflowFormValuesInterface>) => void
    rules: any
}

export const WorkflowFormSteps = ({ formValues, setFormValues, rules }: WorkflowFormStepsProps) => {
    const { t } = useTranslation()
    const loggedInEmployee = useLoggedInEmployee()
    const autoApprover = EmployeeService.getAutoApprover()

    const [isAutoApproverSelected, setIsAutoApproverSelected] = useStateIfMounted(formValues.steps?.[0]?.approver === autoApprover.id)

    const anchorRef = useRef(null)

    // Handles selection of auto approver. By design, if auto approver is selected we do not allow other approvers on the step. Additionally, auto approver can only be selected  on first step.
    useEffect(() => {
        const { steps } = formValues
        const firstStepApprover = formValues.steps?.[0]?.approver
        if (firstStepApprover === ApproverTypeEnum.AUTO_APPROVER) {
            steps[0].otherApprovers = []
            steps[0].operator = undefined
            steps[0].condition = undefined
            setFormValues({ steps })
            setIsAutoApproverSelected(true)
        } else if (isAutoApproverSelected) {
            setIsAutoApproverSelected(false)
        }
    }, [formValues.steps?.[0]?.approver])

    // Request workflow: Enforce net amount condition on second step when auto approver is selected.
    // Folder workflow: Prevent any other step from being created.
    useEffect(() => {
        let { steps } = formValues
        if (steps?.[0]?.approver === ApproverTypeEnum.AUTO_APPROVER && steps[1]) {
            if (formValues.workflowType === WorkflowFormTypeEnum.FOLDER) {
                steps = [steps[0]]
            } else {
                steps[1].condition = WorkflowConditionEnum.NET_AMOUNT
                steps[1].currency = loggedInEmployee.settings.globalCurrency ?? CurrencyEnum.EUR
            }
            setFormValues({ steps })
        }
    }, [formValues.steps?.[0]?.approver, formValues.steps?.length === 2, formValues.steps?.[1]?.condition])

    const handleStepUpdate = (stepIndex: number, stepValues: any) => {
        const { steps } = formValues
        steps[stepIndex] = stepValues
        setFormValues({ steps })
    }

    return (
        <Row gutter={[16, 16]}>
            <Col span={24}>
                <Form.List name="steps">
                    {(fields, { add, remove }) => (
                        <div className="workflows workflow-steps" ref={anchorRef}>
                            <Steps className="" direction="vertical" size="small" current={-1}>
                                {fields.map((_field, stepIndex) => {
                                    const step = formValues?.steps[stepIndex]
                                    return step ? (
                                        <Steps.Step
                                            key={stepIndex}
                                            status="process"
                                            description={
                                                <WorkflowFormStep
                                                    {...{
                                                        stepIndex,
                                                        step,
                                                        onRemove: remove,
                                                        onUpdate: handleStepUpdate,
                                                        isAutoApproverSelected,
                                                        anchorRef,
                                                        rules,
                                                        workflowType: formValues.workflowType,
                                                    }}
                                                />
                                            }
                                        />
                                    ) : (
                                        <></>
                                    )
                                })}
                            </Steps>

                            <Row gutter={[8, 0]}>
                                {(formValues.workflowType !== WorkflowFormTypeEnum.FOLDER || !isAutoApproverSelected) && (
                                    <Button onClick={() => add({ approver: "", otherApprovers: [] })}>
                                        <PlusIcon />
                                        <span className="ml-2">{t("action:workflow.next_step")}</span>
                                    </Button>
                                )}
                            </Row>
                        </div>
                    )}
                </Form.List>
            </Col>
        </Row>
    )
}
